import { Transcript } from './../../core/models/transcripts/transcripts.interface';
import { Recording } from './../../core/models/recordings/recordings.interface';
import { ChangeDetectorRef, Component, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Observable, catchError, combineLatest, firstValueFrom, map, of, shareReplay } from 'rxjs';
import { Survey, SurveyObservation, SurveyPraise } from 'src/app/core/models/surveys/surveys.interface';
import { SurveyService } from './../../core/service/surveys/surveys.service';
import { ActivityService } from './../../core/service/activities/activities.service';
import { Activity } from './../../core/models/activities/activities.interface';
import { RecordingService } from "./../../core/service/recordings/recordings.service";
import { SurveyResult } from 'src/app/core/models/surveys/surveys.interface';
import { Role } from 'src/app/core/models/role';
import { AuthService } from 'src/app/core/service/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UnsavedEditsService } from 'src/app/core/service/unsaved-edits.service';
import { SME_REVIEW_USER_IDS } from 'src/app/core/models/sme-review';

import * as _ from "lodash";
import { SurveyResponseProcessor } from '../../core/models/surveys/surveys.interface';
import { parseLearnerReport, serializeLearnerReport, ParsedLearnerReport, parseCoachSummary, ParsedCoachSummary, CoachSection, CoachSectionKind, trimCoachTip, inlineItemQuotes } from './report-markdown';
import { REPORT_SECTIONS, CARD_TAGLINES, SKILL_DEMONSTRATION } from './report-sections.constants';

var showdown = require("showdown");

interface StepFeedback {
  step0p: SurveyPraise[]; step0o: SurveyObservation[];
  step1p: SurveyPraise[]; step1o: SurveyObservation[];
  step2p: SurveyPraise[]; step2o: SurveyObservation[];
  step3p: SurveyPraise[]; step3o: SurveyObservation[];
  step4p: SurveyPraise[]; step4o: SurveyObservation[];
  step5p: SurveyPraise[]; step5o: SurveyObservation[];
}

// The view model behind data$. hasSurvey distinguishes coach/self reviews
// (which have a surveyresponses doc) from automated reviews (which don't and
// are driven entirely by the AI report).
export interface RecordingReportView extends StepFeedback {
  hasSurvey: boolean;
  // A coach can edit this report: coach/self reviews save to the survey,
  // automated reviews save to the activity doc (needs the activity ids).
  canEditSummary: boolean;
  // A coach-edited copy currently overrides the AI report (enables Reset).
  hasCoachEdits: boolean;
  survey: Survey | null;
  recording: Recording;
  transcript: Transcript;
  aiReportV2: boolean;
  nextStepsV2: any[];
  learnerReportV2: string;
  coachReportV2: string;
  summaryReport: string;
  questions: any[];
  praiseObservationsRows: Array<{ step: number, type: 'praise' | 'observation', code: string, explanation: string, notes: string }>;
  // v2.1 card layout (see report-markdown.ts). isV21 is false for every report
  // whose markdown lacks the v2.1 headings, which keeps the legacy V2 markup.
  isV21?: boolean;
  learnerSections?: ParsedLearnerReport | null;
  // Coach Summary cards; null keeps the single-blob render.
  coachSections?: ParsedCoachSummary | null;
  learnerName?: string;
  recordingDate?: Date | null;
}

// The only users who see the Download SME review icon: see SME_REVIEW_USER_IDS
// (src/app/core/models/sme-review.ts), shared with the PYI reports.

@Component({
  selector: 'app-recording-report',
  templateUrl: './recording-report.component.html',
  styleUrls: ['./recording-report.component.sass']
})
export class RecordingReportComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  activityId: any;

  @Input()
  skillId: any;

  @Input()
  surveyId: any;

  @Input()
  recordingId: any;

  @Input()
  activityOwnerId: any;

  Role: typeof Role = Role;

  public myReport = true;

  public sliceValue = 1;

  public canEditReport = false;

  public aiReportV2 = false;
  public nextStepsV2: string[] = [];
  public learnerReportV2: string = "";
  public coachReportV2: string = "";
  public aiScoring: any = null;

  // Download SME review (Excel icon in the report toolbar). Set from each data$ emission.
  public smeReviewAllowed = false;
  public smeReviewDownloading = false;
  public smeReviewError = '';

  // Agencies whose learners see the user type / level + step results heading
  // on the legacy recording report.
  private static readonly USER_TYPE_HEADER_AGENCY_IDS = ['9Rz8evD3YGrpP1qj2aIl'];

  get showUserTypeHeader(): boolean {
    return RecordingReportComponent.USER_TYPE_HEADER_AGENCY_IDS.includes(this.activityOwner?.agencyId);
  }

  // ---- v2.1 report layout ----
  public readonly reportSections = REPORT_SECTIONS;
  public readonly cardTaglines = CARD_TAGLINES;
  // Material icon shown in each Coach Summary section's chip.
  public readonly coachIcons: { [kind in CoachSectionKind]: string } = {
    concerns: 'report_problem',
    wins: 'thumb_up',
    growth: 'trending_up',
    debrief: 'forum',
    other: 'notes',
  };
  public readonly dotSlots = [1, 2, 3];
  // Interaction Summary renders as a card beside Your Skill Demonstration.
  // Flip to false to show it as plain text above the cards instead.
  public showInteractionSummaryCard = true;
  // Memoized so the template binding keeps a stable object between change detections.
  public skillDemonstration: { level: string, levelLabel: string, dots: number, sentence: string, disclaimer: string } | null = null;
  public activityOwner$: Observable<any>;
  public activityOwner: any = null;
  private mdCache = new Map<string, string>();

  //public recordingId?: string;
  public recording$: Observable<Recording>;
  public survey$: Observable<Survey | undefined>;
  public transcript$: Observable<Transcript>
  public aiReport$: Observable<any>;
  public aiScoring$: Observable<any>;
  public aiReportData: any;
  public srp: SurveyResponseProcessor;
  public currentUser: any;
  public userTypeAndLevel: { userType: string, level: string } = { userType: "", level: "" };

  public showdownConverter = new showdown.Converter();

  public isEditingReport = false;
  // Deep copy of the parsed v2.1 report while a coach edits it, one box per
  // area. The rendered view (surData.learnerSections) is never mutated; Save
  // reassembles this model into markdown and Cancel simply drops it.
  public editModel: ParsedLearnerReport | null = null;
  // Serialized form of editModel when editing started; anything different is an unsaved edit.
  private editBaseline = '';

  public surData: any;

  public reportCheckboxes = [
  ]

  // Keys of the next steps the learner has selected. Keyed (not reference-based)
  // so selections survive a reload, when the saved copies are no longer the same
  // object instances as the freshly-loaded nextStepsV2.
  public selectedNextStepKeys = new Set<string>();
  private nextStepsInitialized = false;
  // Guards the note-seeding Firestore write so it fires once per input change,
  // not once per data$ emission.
  private notesSeeded = false;
  public activity$: Observable<Activity>;


  private readonly feedbackTypeOrder: Record<string, number> = {
    praise: 0,
    observation: 1,
  };

  public reportNotes?: {
    step0?: {
      coachesNote?: string,
      learnersNote?: string
    },
    step1?: {
      coachesNote?: string,
      learnersNote?: string
    },
    step2?: {
      coachesNote?: string,
      learnersNote?: string
    },
    step3?: {
      coachesNote?: string,
      learnersNote?: string
    },
    step4?: {
      coachesNote?: string,
      learnersNote?: string
    },
    step5?: {
      coachesNote?: string,
      learnersNote?: string
    }
  } = {
      step0: {
        coachesNote: '',
        learnersNote: ''
      },
      step1: {
        coachesNote: '',
        learnersNote: ''
      },
      step2: {
        coachesNote: '',
        learnersNote: ''
      },
      step3: {
        coachesNote: '',
        learnersNote: ''
      },
      step4: {
        coachesNote: '',
        learnersNote: ''
      },
      step5: {
        coachesNote: '',
        learnersNote: ''
      }
    };
  summaryMD: string;

  hasSelectedCheckbox(type: string, code: string): boolean {
    return this.reportCheckboxes.includes(type + ":" + code);
  }

  private buildPraiseObservationRows(sur: Survey): Array<{ step: number, type: 'praise' | 'observation', code: string, explanation: string, notes: string }> {
    const praise = (sur.processedResults?.praise ?? []);

    const observations = (sur.processedResults?.observations ?? []);

    const rows = [
      ...praise.map((p: any) => ({
        step: p.Step,
        type: 'praise' as const,
        code: p.Code,
        explanation: p.Explanation ?? p.Strength ?? '',
        notes: p.Notes ?? '',
      })),
      ...observations.map((o: any) => ({
        step: o.Step,
        type: 'observation' as const,
        code: o.Code,
        explanation: o.Explanation ?? o.Feedback ?? '',
        notes: o.Notes ?? '',
      })),
    ];

    return rows.sort((a, b) => {
      if (a.step !== b.step) return a.step - b.step;
      const typeCmp = (this.feedbackTypeOrder[a.type] ?? 99) - (this.feedbackTypeOrder[b.type] ?? 99);
      if (typeCmp !== 0) return typeCmp;
      return (a.code ?? '').localeCompare(b.code ?? '');
    });
  }

  toggleCheckbox(type: string, code: string, event: any): void {

    if (event.target.checked) {
      this.reportCheckboxes.push(type + ":" + code);
    } else if (this.reportCheckboxes.includes(type + ":" + code)) {
      this.reportCheckboxes = this.reportCheckboxes.filter((item) => item !== type + ":" + code);
    }
  }

  // Stable identity for a next step so selections can be matched across reloads.
  private nextStepKey(step: any): string {
    if (!step) return '';
    return [step.priority ?? '', step.target?.step ?? '', step.action ?? ''].join('|');
  }

  // Seed the learner's selections from the next steps previously saved on the
  // activity. Runs once per set of inputs so live Firestore updates (or the
  // learner's own in-progress toggles) don't clobber the working selection.
  private applySavedNextSteps(activity: Activity | null): void {
    if (this.nextStepsInitialized) return;
    const saved = activity?.nextSteps || [];
    this.selectedNextStepKeys = new Set(saved.map((s: any) => this.nextStepKey(s)));
    this.nextStepsInitialized = true;
  }

  isNextStepSelected(step: any): boolean {
    return this.selectedNextStepKeys.has(this.nextStepKey(step));
  }

  toggleNextStep(step: any, event: any): void {
    const key = this.nextStepKey(step);
    if (event.target.checked) {
      this.selectedNextStepKeys.add(key);
    } else {
      this.selectedNextStepKeys.delete(key);
    }
  }

  saveNextSteps() {
    // Resolve the selected keys back to the full next step objects from the form.
    const selectedSteps = (this.nextStepsV2 || []).filter((step: any) =>
      this.selectedNextStepKeys.has(this.nextStepKey(step)));
    console.log("Selected next steps:", selectedSteps);

    this.snackBar.open('Next steps saved successfully.', '', {
      duration: 5000,
      panelClass: ['customSnackbar']
    });
    
    console.log(this.activityOwnerId, this.skillId, this.activityId)
    this.ActivityService.saveNextSteps(this.activityOwnerId, this.skillId, this.activityId, selectedSteps);
    this.ActivityService.markActivityCompletedCallable(this.activityOwnerId, this.skillId, this.activityId);

  }


  public data$: Observable<RecordingReportView>

  constructor(
    private RecordingService: RecordingService,
    private SurveyService: SurveyService,
    public authService: AuthService,
    private ActivityService: ActivityService,
    private snackBar: MatSnackBar,
    private unsavedEdits: UnsavedEditsService,
    private cdr: ChangeDetectorRef
  ) { }

  private destroyed = false;

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  // The SME download callable settles outside change detection (a report parent can
  // detach its detector), so refresh this view by hand once it's safe to.
  private refreshView(): void {
    if (!this.destroyed) {
      this.cdr.detectChanges();
    }
  }

  public skillStepTable = [
    { skillName: 'Role Clarification', skillInitials: 'rc', steps: [0, 1, 2, 3, 4, 5], maxNubm: 23, maxIubm: 19, maxAubm: 15, type: 'woc' },
    { skillName: 'Effective Use of Reinforcement', skillInitials: 'eur', steps: [0, 1, 2, 3, 4], maxNubm: 21, maxIubm: 13, maxAubm: 11, type: 'woc' },
    { skillName: 'Effective Use of Disapproval', skillInitials: 'eud', steps: [0, 1, 2, 3, 4, 5], maxNubm: 35, maxIubm: 41, maxAubm: 26, type: 'woc' },
    { skillName: 'Teaching the Cognitive Model', skillInitials: 'tcm', steps: [0, 1, 2, 3, 4], maxNubm: 33, maxIubm: 40, maxAubm: 27, type: 'woc' },
    { skillName: 'Role Clarification', skillInitials: 'rc', steps: [0, 1, 2, 3, 4, 5], maxNubm: 23, maxIubm: 19, maxAubm: 15, type: 'wac' },
    { skillName: 'Effective Use of Reinforcement', skillInitials: 'eur', steps: [0, 1, 2, 3, 4], maxNubm: 21, maxIubm: 13, maxAubm: 11, type: 'wac' },
    { skillName: 'Effective Use of Disapproval', skillInitials: 'eud', steps: [0, 1, 2, 3, 4, 5], maxNubm: 33, maxIubm: 40, maxAubm: 27, type: 'wac' },
    { skillName: 'Teaching the Cognitive Model', skillInitials: 'tcm', steps: [0, 1, 2, 3, 4], maxNubm: 49, maxIubm: 55, maxAubm: 33, type: 'wac' },
    // Add more skills and steps as needed FIXME WES FIX ME TCM NUMBERS
  ];

  getMaxValue(skillName: string, level: string, type: string): number {
    let skill = this.skillStepTable.find((item: any) => item.skillName === skillName && item.type === type);
    if (!skill) {
      skill = this.skillStepTable.find((item: any) => item.skillInitials === skillName && item.type === type);
    }

    if (skill) {
      if (level == "nubm") {
        return skill.maxNubm;
      } else if (level == "iubm") {
        return skill.maxIubm;
      } else if (level == "aubm") {
        return skill.maxAubm;
      }
    }
    return 0;
  }

  hasStep(skillName: string, step: number): boolean {
    // Assuming you have a datatable called "datatable" with the necessary data

    // Find the skill in the datatable
    const skill = this.skillStepTable.find((item: any) => item.skillName === skillName);

    // If the skill is found and the step exists, return true
    if (skill && skill.steps.includes(step)) {
      return true;
    }

    // Otherwise, return false
    return false;
  }

  ngOnInit(): void {

    console.log("********" + this.surveyId);
    const dataValues = [
      "NUBM:1.1", "IUBM:2.1", "IUBM:2.2", "AUBM:3.1",
      // ... (include all other data values here)
      "AUBM:3.10", "AUBM:3.11"
    ];


    if (this.activityOwnerId != this.authService.currentUserValue.id) {
      this.sliceValue = 20;
      this.myReport = false;
    }

    if (this.myReport) {
      this.canEditReport = false;
    }
    
    if (this.authService.checkUserRoles([Role.Coach, Role.PraxusAdmin])) {
      this.canEditReport = true;
    }

    /*
    const table = document.querySelector('table-wes');
  
    dataValues.forEach(value => {
        const tr = document.createElement('tr');
  
        const tdValue = document.createElement('td');
        tdValue.textContent = value;
        tr.appendChild(tdValue);
  
        for (let i = 0; i < 3; i++) {
            const td = document.createElement('td');
            if (Math.random() < 0.5) {
                td.textContent = '✓';
                td.classList.add('green');
            }
            tr.appendChild(td);
        }
  
        table.appendChild(tr);
    });
  
    */

  }

  returnTrueXPercentOfTheTime(x: number): boolean {
    const random = Math.random() * 100;
    return random <= x;
  }


  /**
   * 
   * create a function that takes in three arrays, new user "nubm". intermediate user "iubm", and advanced user "aubm"
   * also passed in will be the max values of the count of nubm, iubm, and aubm
   * if 80% of the nubm is achvied the user is new user, 
   * if 80% of the iubm is achvied the user is intermediate user,
   * if 80% of the aubm is achvied the user is advanced user,
   * once the user is determined, the function will return the user type
   * also the function will return low, medium, or high based on the percentage of the next level
   */
  determineUserType(nubm: any[], iubm: any[], aubm: any[], maxNubm: number, maxIubm: number, maxAubm: number): { userType: string, level: string } {
    const nubmAchieved = nubm?.length / maxNubm;
    const iubmAchieved = iubm?.length / maxIubm;
    const aubmAchieved = aubm?.length / maxAubm;

    console.log(nubmAchieved, iubmAchieved, aubmAchieved, nubm?.length, maxNubm, iubm?.length, maxIubm, aubm?.length, maxAubm);

    if (iubmAchieved >= 0.8 && nubmAchieved >= 0.8) {
      return { userType: "advanced user", level: this.getLevel(aubmAchieved) };
    } else if (nubmAchieved >= 0.8) {
      return { userType: "intermediate user", level: this.getLevel(iubmAchieved) };
    } else {
      return { userType: "new user", level: this.getLevel(nubmAchieved) };
    }
  }

  private getLevel(percentage: number): string {
    console.log(percentage);
    if (percentage < 0.5) {
      return "low";
    } else if (percentage < 0.7) {
      return "medium";
    } else {
      return "high";
    }
  }

  private undefinedToEmpty(val) {
    return val ?? "";
  }


  async ngOnChanges(changes: SimpleChanges) {

    this.currentUser = await firstValueFrom(this.authService.currentUser);
    console.log(this.currentUser);

    console.log("******** surveyId: " + this.surveyId + ", recordingId: " + this.recordingId);

    // Reset so saved next steps re-seed the selection for the new inputs.
    this.nextStepsInitialized = false;
    this.notesSeeded = false;
    this.reportViewedWritten = false;

    this.recording$ = this.RecordingService.getRecordingById(this.recordingId);
    // Automated reviews have no survey response doc, so skip the lookup.
    this.survey$ = this.surveyId
      ? this.SurveyService.getSurveyById(this.surveyId)
      : of(undefined);
    this.transcript$ = this.RecordingService.getTranscriptById(this.recordingId);
    if (this.surveyId) {
      this.srp = await this.SurveyService.getSurveyResponseProcessor(this.surveyId);
    }

    this.aiReport$ = this.RecordingService.getRecordingAiReportResults(this.recordingId);
    this.aiScoring$ = this.RecordingService.getRecordingAiRecordingScoring(this.recordingId);

    this.activity$ = (this.activityOwnerId && this.skillId && this.activityId)
      ? this.authService.getActivityById(this.activityOwnerId, this.skillId, this.activityId)
      : of(null);

    // Learner shown in the v2.1 banner. A read failure must not take the whole report down.
    this.activityOwner$ = this.activityOwnerId
      ? this.authService.getUserById(this.activityOwnerId).pipe(catchError(() => of(null)))
      : of(null);

    this.data$ = combineLatest([this.recording$, this.survey$, this.transcript$, this.aiReport$, this.aiScoring$, this.activity$, this.activityOwner$]).pipe(
      map(([rec, sur, trans, aiReport, aiScoring, activity, owner]) => {
        this.activityOwner = owner;
        this.maybeMarkReportViewed(activity);
        this.applyAiReportState(aiReport, aiScoring, activity);
        this.smeReviewAllowed = this.canDownloadSmeReview(activity, aiScoring);

        console.log("sur: " + sur);
        console.dir(sur);

        const view = sur
          ? this.buildSurveyView(sur, rec, trans)
          : this.buildAiOnlyView(rec, trans, activity);

        return this.surData = this.applyV21Layout(view, rec, owner);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  // Guards a single reportViewedAt write per input set; activity$ is live so the
  // next emission also carries the field.
  private reportViewedWritten = false;

  // Stamp reportViewedAt the first time the activity owner opens a published
  // report. Coaches/admins viewing a learner never match the owner id, and the
  // survey-list mount passes no owner id, so neither writes.
  private maybeMarkReportViewed(activity: Activity | null): void {
    if (!activity || this.reportViewedWritten) {
      return;
    }
    if (!this.activityOwnerId || !this.skillId || !this.activityId) {
      return;
    }
    if (this.activityOwnerId != this.authService.currentUserValue?.id) {
      return;
    }
    if (activity.reviewStatus != 'Published' || activity.reportViewedAt) {
      return;
    }
    this.reportViewedWritten = true;
    this.ActivityService.markReportViewed(this.activityOwnerId, this.skillId, this.activityId)
      .catch((err) => console.error('markReportViewed failed', err));
  }

  private applyAiReportState(aiReport: any, aiScoring: any, activity: Activity | null): void {
    console.log("AI Report:", aiReport);
    this.aiReportData = aiReport;

    this.nextStepsV2 = this.aiReportData?.results?.recordingReport?.result?.next_steps || [];

    // Pre-check the next steps the learner previously saved on the activity
    // (and drives the read-only checkmarks shown to other viewers).
    this.applySavedNextSteps(activity);

    this.learnerReportV2 = this.aiReportData?.results?.recordingReport?.result?.learner_report_md || "";
    this.coachReportV2 = this.aiReportData?.results?.recordingReport?.result?.coach_addendum_md || "";

    this.aiReportV2 = this.aiReportData?.results?.recordingReport !== undefined && this.aiReportData?.results?.recordingReport !== null;

    if (this.aiReportV2) {
      this.aiScoring = aiScoring;
      console.log("AI aiScoring:", this.aiScoring);
    }
    this.skillDemonstration = this.aiReportV2 ? this.buildSkillDemonstration() : null;
  }

  // ---- v2.1 report layout ----

  // Attaches the v2.1 card data to the view. Only markdown carrying the v2.1
  // headings gets isV21 = true; everything else renders the legacy V2 markup.
  private applyV21Layout(view: RecordingReportView, rec: Recording, owner: any): RecordingReportView {
    const sections = this.aiReportV2 ? parseLearnerReport(view.summaryReport) : null;
    view.learnerSections = sections;
    view.isV21 = !!sections;
    view.coachSections = parseCoachSummary(view.coachReportV2);
    if (view.coachSections) {
      // "No concerns came up in this session." is noise for the coach; drop the card.
      view.coachSections.sections = view.coachSections.sections.filter(sec =>
        !(sec.kind === 'concerns' && this.isCoachNote(sec) && (!sec.body.trim() || /^\s*no\b/i.test(sec.body))));
      // The debrief plan reads first (after any concerns); the wins and growth
      // items are its supporting detail. Unknown sections keep their place at the end.
      const order: CoachSectionKind[] = ['concerns', 'debrief', 'wins', 'growth', 'other'];
      view.coachSections.sections = view.coachSections.sections
        .map((sec, i) => ({ sec, i }))
        .sort((a, b) => order.indexOf(a.sec.kind) - order.indexOf(b.sec.kind) || a.i - b.i)
        .map(x => x.sec);
    }
    view.learnerName = owner?.fullName || rec?.createdBy?.fullName || '';
    view.recordingDate = this.toDate(rec?.recordedDate);
    return view;
  }

  // Coach Summary markdown -> HTML. An italic sentence on a line of its own
  // (the "Encourage by…" / "Coach this…" lines, prompt rule C6) is tagged
  // coach-tip and loses its fixed opener; inline italics are left alone.
  coachMd(text: string): string {
    if (!text) { return ''; }
    const key = 'coach:' + text;
    let html = this.mdCache.get(key);
    if (html === undefined) {
      html = this.showdownConverter.makeHtml(text).replace(
        /(^|\n|<p>|<li>)<em>((?:(?!<\/?em>)[\s\S])*)<\/em>(?=<\/p>|<\/li>|\n|$)/g,
        (_m: string, lead: string, tip: string) => `${lead}<em class="coach-tip">${trimCoachTip(tip)}</em>`);
      this.mdCache.set(key, html);
    }
    return html;
  }

  // A section that is one plain sentence ("No concerns came up in this session.")
  // renders as a muted note rather than an item list.
  isCoachNote(section: CoachSection): boolean {
    const body = (section.body || '').trim();
    return !body || (!/\n/.test(body) && !/^\s*([-*+]|\d+\.)\s/.test(body) && body.indexOf('**') === -1);
  }

  // recordedDate is a Firestore Timestamp on saved recordings; tolerate strings too.
  private toDate(value: any): Date | null {
    if (!value) { return null; }
    if (value instanceof Date) { return value; }
    if (typeof value.toDate === 'function') { return value.toDate(); }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  // "Your Skill Demonstration" card. Level keeps the banner's precedence (derived
  // from scoringSummary percentages, then the engine's promotion result, then the
  // session assessment). Low / Medium / High granularity maps to 1 / 2 / 3 dots.
  private buildSkillDemonstration(): { level: string, levelLabel: string, dots: number, sentence: string, disclaimer: string } | null {
    const derived = this.derivedSkillAssessment;
    const level = derived?.level ?? this.promotion?.demonstratedLevel ?? this.scoring?.sessionAssessment?.detectedSkillLevel;
    if (!level) { return null; }

    const dots = SKILL_DEMONSTRATION.dots(derived?.granularity ?? this.promotion?.demonstratedGranularity);
    const levelLabel = SKILL_DEMONSTRATION.levelLabel(level);

    return {
      level,
      levelLabel,
      dots,
      sentence: SKILL_DEMONSTRATION.sentence(dots, levelLabel),
      disclaimer: SKILL_DEMONSTRATION.disclaimer,
    };
  }

  // Memoized markdown -> HTML for the v2.1 cards; the template calls this on every change detection.
  md(text: string): string {
    if (!text) { return ''; }
    let html = this.mdCache.get(text);
    if (html === undefined) {
      html = this.showdownConverter.makeHtml(text);
      this.mdCache.set(text, html);
    }
    return html;
  }

  // Step item lists: an improvement's evidence quote reads as part of its item.
  itemsMd(text: string): string {
    return this.md(inlineItemQuotes(text));
  }

  // Automated reviews have no survey response; the report is driven entirely
  // by the AI report/scoring, so no survey-derived state is touched here.
  // A coach-edited copy on the activity doc takes precedence over the AI report.
  private buildAiOnlyView(rec: Recording, trans: Transcript, activity: Activity | null): RecordingReportView {
    this.reportCheckboxes = [];

    const coachesReport = this.aiReportV2 ? (activity?.coachesReport || '') : '';

    return {
      hasSurvey: false,
      canEditSummary: this.aiReportV2 && this.hasActivityIds,
      hasCoachEdits: !!coachesReport,
      aiReportV2: this.aiReportV2,
      nextStepsV2: this.nextStepsV2,
      learnerReportV2: this.learnerReportV2,
      coachReportV2: this.coachReportV2,
      recording: rec,
      survey: null,
      summaryReport: coachesReport || (this.aiReportV2 ? this.learnerReportV2 : ''),
      transcript: trans,
      questions: [],
      praiseObservationsRows: [],
      ...this.buildStepFeedback([], []),
    };
  }

  private buildSurveyView(sur: Survey, rec: Recording, trans: Transcript): RecordingReportView {
    const nubm = sur.nubm;
    const iubm = sur.iubm;
    const aubm = sur.aubm;
    const maxNubm = this.getMaxValue(sur.skill, "nubm", sur.type);
    const maxIubm = this.getMaxValue(sur.skill, "iubm", sur.type);
    const maxAubm = this.getMaxValue(sur.skill, "aubm", sur.type);

    this.reportCheckboxes = sur.selectedFeedback || [];

    if (sur.raw?.url_variables?.type?.value == "woc") {
      let selectedFeedback: string[] = [];
      sur.processedResults.praise.forEach((element: SurveyPraise) => {
        selectedFeedback.push('p:' + element.Code);
      });
      sur.processedResults.observations.forEach((element: SurveyObservation) => {
        selectedFeedback.push('o:' + element.Code);
      });
      this.reportCheckboxes = selectedFeedback;
    }

    this.seedReportNotes(sur);

    this.userTypeAndLevel = this.determineUserType(nubm, iubm, aubm, maxNubm, maxIubm, maxAubm);
    if (sur.userType) {
      console.log("userType from RESULTS: " + sur.userType);
      this.userTypeAndLevel.userType = sur.userType;
      this.userTypeAndLevel.level = sur.level;
    }

    var summaryReport = '';
    // check for aiReport or coachesReport
    if (sur.coachesReport) {
      summaryReport = sur.coachesReport;
    } else if (sur.aiReport) {
      summaryReport = JSON.parse(sur.aiReport || '{}')?.choices?.[0]?.message?.content ?? '';
    } else if (this.aiReportV2) {
      summaryReport = this.learnerReportV2;
    }

    return {
      hasSurvey: true,
      canEditSummary: true,
      hasCoachEdits: !!sur.coachesReport,
      aiReportV2: this.aiReportV2,
      nextStepsV2: this.nextStepsV2,
      learnerReportV2: this.learnerReportV2,
      coachReportV2: this.coachReportV2,
      recording: rec,
      survey: sur,
      summaryReport: summaryReport,
      transcript: trans,
      questions: Object.values(sur.raw?.survey_data ?? {}),
      praiseObservationsRows: this.buildPraiseObservationRows(sur),
      ...this.buildStepFeedback(sur.processedResults?.praise ?? [], sur.processedResults?.observations ?? []),
    };
  }

  private seedReportNotes(sur: Survey): void {
    if (sur.notes) {
      this.reportNotes = sur.notes;
      return;
    }

    console.log("No notes found");
    // get notes from the survey and place in the report notes at the correct step
    if (sur.type == "wac" && sur.skill == "eur") {
      console.log("WAC - EUR");
      this.reportNotes = {
        step0: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(9))
        },
        step1: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(37))
        },
        step2: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(61)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(62)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(66))
        },
        step3: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(87)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(88)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(92))
        },
        step4: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(122)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(125))
        },
        step5: {
          coachesNote: '',
          learnersNote: ''
        }
      };
    } else if (sur.type == "wac" && sur.skill == "rc") {
      console.log("WAC - RC");
      this.reportNotes = {
        step0: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(160)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(161))
        },
        step1: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(43))
        },
        step2: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(62))
        },
        step3: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(74)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(76)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(79)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(82))
            + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(89)) + " " + this.undefinedToEmpty(this.srp.getSurveyAnswer(90))
        },
        step4: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(119))
        },
        step5: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(136))
        }
      };
    } else if (sur.type == "wac" && sur.skill == "eud") {
      console.log("WAC - EUD");
      this.reportNotes = {
        step0: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(7))
        },
        step1: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(33))
        },
        step2: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(243))
        },
        step3: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(276))
        },
        step4: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(299))
        },
        step5: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(316))
        }
      };
    } else if (sur.type == "wac" && sur.skill == "tcm") {
      console.log("WAC - TCM");
      this.reportNotes = {
        step0: {
          coachesNote: '',
          learnersNote: ''
        },
        step1: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(29))
        },
        step2: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(76))
        },
        step3: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(129))
        },
        step4: {
          coachesNote: '',
          learnersNote: this.undefinedToEmpty(this.srp.getSurveyAnswer(149))
        },
        step5: {
          coachesNote: '',
          learnersNote: ''
        }
      };
    }
    if (this.surveyId && !this.notesSeeded) {
      this.notesSeeded = true;
      this.SurveyService.saveSelectedFeedback(this.surveyId, this.reportCheckboxes, this.reportNotes);
    }
  }

  private buildStepFeedback(praise: SurveyPraise[], observations: SurveyObservation[]): StepFeedback {
    const canSeeAll = this.authService.checkUserRoles([Role.Coach, Role.PraxusAdmin]);
    const visible = (type: 'p' | 'o', item: any) =>
      canSeeAll || (this.myReport && this.hasSelectedCheckbox(type, item.Code));

    const out: any = {};
    for (let step = 0; step <= 5; step++) {
      out[`step${step}p`] = praise.filter(p => p.Explanation && p.Step == step && visible('p', p));
      out[`step${step}o`] = observations.filter(o => o.Explanation && o.Step == step && visible('o', o));
    }
    return out;
  }

  hasComment(fb: any): boolean {
    if (this.surData.survey?.raw.survey_data[fb.Question].comments != undefined) {
      return true;
    }
    return false;
  }

  getComment(fb: any): string {
    if (this.surData.survey?.raw.survey_data[fb.Question].comments != undefined) {
      return this.surData.survey.raw.survey_data[fb.Question].comments;
    }
    return "";
  }


  getRand(ob: any): Boolean {

    return (Math.random() > ob)
  }

  processAnswer(value: any): string {
    if (value.answer != undefined) {
      return value.answer;
    }

    let result = ""
    if (value.options != undefined) {
      _.forOwn(value.options, (v2, k2) => {
        if (v2?.answer != null && v2?.option != null) {
          result += `${v2.option}</br>`;
        }
      })
    }

    return result;
  }

  levelClass(ob: any): string {

    if (ob?.Code?.startsWith("l1")) {
      return "level1";
    } else if (ob?.Code?.startsWith("l2")) {
      return "level2";
    } else if (ob?.Code?.startsWith("l3")) {
      return "level3";
    }

    return "";
  }


  levelText(ob: any): string {

    if (ob?.Code?.startsWith("l1")) {
      return "New User";
    } else if (ob?.Code?.startsWith("l2")) {
      return "Intermediate User";
    } else if (ob?.Code?.startsWith("l3")) {
      return "Advanced User";
    }

    return "";
  }


  getBackgroundColor(number: number): string {
    if (number % 2 === 0) {
      return '#fff'; // Even number
    } else {
      return '#F8F8F8'; // Odd number
    }
  }

  saveReport() {
    this.SurveyService.saveSelectedFeedback(this.surveyId, this.reportCheckboxes, this.reportNotes);
    this.snackBar.open('Changes saved successfully.', '', {
      duration: 5000,
      panelClass: ['customSnackbar']
    });
  }

  publishReport() {
    this.SurveyService.saveSelectedFeedback(this.surveyId, this.reportCheckboxes, this.reportNotes);
    this.SurveyService.publishRecordingReport(this.activityOwnerId, this.skillId, this.activityId);
    this.snackBar.open('Report published to Learner.', '', {
      duration: 5000,
      panelClass: ['customSnackbar']
    });
  }

  publishLearnerReprot () {
    this.SurveyService.publishRecordingReport(this.activityOwnerId, this.skillId, this.activityId);
    this.snackBar.open('Report published to Learner.', '', {
      duration: 5000,
      panelClass: ['customSnackbar']
    });
  }

  createAIReport() {
    this.SurveyService.createAIReport(this.activityOwnerId, this.skillId, this.activityId);
    this.snackBar.open('Report summary creation in progress.', '', {
      duration: 5000,
      panelClass: ['customSnackbar']
    });
  }

  saveSummary() {
    this.SurveyService.saveSummary(this.surveyId, this.summaryMD);
    this.snackBar.open('Report summary saved successfully.', '', {
      duration: 5000,
      panelClass: ['customSnackbar']
    });
  }

  async saveLearnerReport() {
    if (!this.surveyId && !this.hasActivityIds) {
      this.snackBar.open('This report has no record to save edits to.', '', { duration: 5000, panelClass: ['customSnackbar'] });
      return;
    }
    // v2.1: reassemble the per-area edit model; legacy: the whole-report editor's text.
    const md = this.editModel ? serializeLearnerReport(this.editModel) : this.summaryMD;
    try {
      // Coach/self reviews keep edits on the survey; automated reviews on the activity.
      if (this.surveyId) {
        await this.SurveyService.saveSummary(this.surveyId, md);
      } else {
        await this.ActivityService.saveCoachesReport(this.activityOwnerId, this.skillId, this.activityId, md);
      }
    } catch (error) {
      console.error('saveLearnerReport failed', error);
      this.snackBar.open('Saving the report failed. Please try again.', '', { duration: 5000, panelClass: ['customSnackbar'] });
      return;
    }
    this.summaryMD = md;
    this.editModel = null;
    this.editBaseline = '';
    this.isEditingReport = false;
    this.snackBar.open('Report saved successfully.', '', {
      duration: 5000,
      panelClass: ['customSnackbar']
    });
  }

  async cancelEdit() {
    if (this.hasUnsavedEdits && !(await this.confirm('Discard your unsaved edits to this report?', 'Discard'))) {
      return;
    }
    this.summaryMD = this.surData?.summaryReport ?? '';
    this.editModel = null;
    this.editBaseline = '';
    this.isEditingReport = false;
  }

  // True while the coach is editing and the boxes no longer match what was loaded.
  // Only read from event handlers and the route guard, never from template bindings.
  get hasUnsavedEdits(): boolean {
    return this.isEditingReport && !!this.editModel && serializeLearnerReport(this.editModel) !== this.editBaseline;
  }

  // Route-leave hook, called through RecordingComponent.canDeactivate() and the
  // PendingChangesGuard on the activity-view route.
  canDeactivate(): Promise<boolean> | boolean {
    if (!this.hasUnsavedEdits) { return true; }
    return this.confirm('You have unsaved report edits. Leave without saving?', 'Leave');
  }

  // Tab close / reload: the browser shows its own generic prompt.
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedEdits) {
      event.preventDefault();
      event.returnValue = true;
    }
  }

  private confirm(message: string, confirmLabel: string, cancelLabel = 'Keep editing'): Promise<boolean> {
    return this.unsavedEdits.confirm(message, confirmLabel, cancelLabel);
  }

  // Drops the coach-edited copy so the AI report shows through again (the
  // read precedence in buildSurveyView / buildAiOnlyView prefers coachesReport
  // while it exists).
  async resetToAiReport() {
    if (!this.surveyId && !this.hasActivityIds) { return; }
    if (!(await this.confirm('Discard your edits and restore the original version of this report?', 'Reset', 'Cancel'))) { return; }
    try {
      if (this.surveyId) {
        await this.SurveyService.clearCoachesReport(this.surveyId);
      } else {
        await this.ActivityService.clearCoachesReport(this.activityOwnerId, this.skillId, this.activityId);
      }
    } catch (error) {
      console.error('resetToAiReport failed', error);
      this.snackBar.open('Resetting the report failed. Please try again.', '', { duration: 5000, panelClass: ['customSnackbar'] });
      return;
    }
    this.editModel = null;
    this.isEditingReport = false;
    this.snackBar.open('Report reset to the original version.', '', { duration: 5000, panelClass: ['customSnackbar'] });
  }

  // Owner/skill/activity ids locate the activity doc that holds automated-review edits.
  private get hasActivityIds(): boolean {
    return !!(this.activityOwnerId && this.skillId && this.activityId);
  }

  // Keeps each edit box's editor instance stable across change detection.
  trackByIndex(index: number): number {
    return index;
  }

  onMarkdownChange(md: string) {
    this.summaryMD = md;
    // do whatever you want here (save, debounce, etc.)
  }

  trackByStepId(_index: number, step: { id?: string }): string {
  return step?.id ?? String(_index);
}

get nextStepsSorted(): any[] {
  const steps = this.aiReportData?.result?.next_steps ?? [];
  return [...steps].sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
}

// Same gate as the exportSmeReview callable: only the users in SME_REVIEW_USER_IDS, on
// Published recordings scored by the v2 pipeline (the recordingScoring row's prompt
// version is anything but the v1 pipeline's "v1").
private canDownloadSmeReview(activity: Activity | null, aiScoring: any): boolean {
  if (!SME_REVIEW_USER_IDS.includes(this.authService.currentUserValue?.id)) return false;
  const scoring = aiScoring?.results?.recordingScoring;
  const isV2 = scoring?.status == 'complete' && !!scoring?.promptVersion && scoring.promptVersion != 'v1';
  return !!activity && activity.reviewStatus == 'Published' && isV2;
}

async downloadSmeReview() {
  this.smeReviewDownloading = true;
  this.smeReviewError = '';
  try {
    const { filename, contentBase64 } = await this.RecordingService.exportSmeReview(this.activityOwnerId, this.skillId, this.activityId);
    const bytes = Uint8Array.from(atob(contentBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoking right away can cancel the download in Safari
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err: any) {
    console.error('exportSmeReview failed', err);
    // failed-precondition carries a readable reason (no recording, scoring not complete, v1).
    const code = String(err?.code || '').replace('functions/', '');
    const message =
      code == 'failed-precondition' ? err.message :
      code == 'permission-denied' || code == 'unauthenticated' ? 'You do not have access to the SME review for this activity.' :
      'The SME review could not be downloaded.';
    this.smeReviewError = message;
  } finally {
    this.smeReviewDownloading = false;
    this.refreshView();
  }
}

editReport() {
  const sections = this.surData?.learnerSections as ParsedLearnerReport | null | undefined;
  if (sections) {
    // Per-area edit boxes: every always-shown box needs a bindable string, so
    // absent areas become '' (the serializer omits them again when left empty).
    const model: ParsedLearnerReport = _.cloneDeep(sections);
    model.interactionSummary = model.interactionSummary ?? '';
    model.interactionStrengths = model.interactionStrengths ?? '';
    model.developmentAreas = model.developmentAreas ?? '';
    model.steps = (model.steps || []).map(step => ({
      ...step,
      whatHappened: step.whatHappened ?? '',
      didWell: step.didWell ?? '',
      canImprove: step.canImprove ?? '',
    }));
    model.extraSections = model.extraSections || [];
    this.editModel = model;
    this.editBaseline = serializeLearnerReport(model);
  } else {
    this.editModel = null;
    this.editBaseline = '';
  }
  this.isEditingReport = true;
}

get scoring(): any {
  return this.aiScoring?.results?.recordingScoring?.result
      ?? this.aiScoring?.result;
}

get promotion(): any {
  return this.scoring?.scoringSummary?.promotion;
}

// Level/granularity derived from scoringSummary: start at Basic and move up a
// level each time the current level scores >= 80%, showing the granularity of
// the level landed on. Compares raw met/applicable, not the 3dp-rounded
// percentage. Must match the backend's skillDemonstrationFromScoring
// (functions/src/helper/skillDemonstration.ts), which writes the same values
// onto activity.skillDemonstration for the dashboards.
get derivedSkillAssessment(): any {
  const summary = this.scoring?.scoringSummary;
  if (!summary) { return null; }

  const raw = (tier: any): number => {
    if (typeof tier?.metCount === 'number' && typeof tier?.totalApplicable === 'number') {
      return tier.totalApplicable > 0 ? tier.metCount / tier.totalApplicable : 0;
    }
    return tier?.percentage ?? 0;
  };

  var level = 'Basic';
  var granularity = summary.basic?.granularity;

  if (raw(summary.basic) >= 0.8) {
    level = 'Intermediate';
    granularity = summary.intermediate?.granularity;

    if (raw(summary.intermediate) >= 0.8) {
      level = 'Advanced';
      granularity = summary.advanced?.granularity;
    }
  }

  return { level: level, granularity: granularity };
}

get steps(): any[] {
  return (this.scoring?.steps ?? []).filter((s: any) => s.stepNumber !== 0);
}

get stepsInitiatedPercent(): number {
  const steps = this.steps;
  if (!steps.length) { return 0; }
  return Math.round((steps.filter(s => s.detected).length / steps.length) * 100);
}

}
